// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Card, Checkbox, Col, Form, Input, Popover, Row, Select, Steps} from "antd";
import * as AuthBackend from "./AuthBackend";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Util from "./Util";
import * as Setting from "../Setting";
import i18next from "i18next";
import {SendCodeInput} from "../common/SendCodeInput";
import * as UserBackend from "../backend/UserBackend";
import {ArrowLeftOutlined, CheckCircleOutlined, KeyOutlined, LockOutlined, SolutionOutlined, UnlockOutlined, UserOutlined} from "@ant-design/icons";
import CustomGithubCorner from "../common/CustomGithubCorner";
import {withRouter} from "react-router-dom";
import * as PasswordChecker from "../common/PasswordChecker";

const {Option} = Select;

class ForgetPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      applicationName: props.applicationName ?? props.match.params?.applicationName,
      msg: null,
      name: props.account ? props.account.name : "",
      username: props.account ? props.account.name : "",
      phone: "",
      email: "",
      dest: "",
      isVerifyTypeFixed: false,
      verifyType: "", // "email", "phone"
      current: 0,
      operationType: "", // "changePassword", "resetPassword", "unlockAccount"
      passwordPopover: null,
      passwordPopoverOpen: false,
      code: "",
      isAtHomePage: true,
      resetEmailPassword: false, // 新增状态：是否重置邮箱密码，默认勾选
    };
    this.form = React.createRef();
  }

  componentDidMount() {
    if (this.getApplicationObj() === undefined) {
      if (this.state.applicationName !== undefined) {
        this.getApplication();
      } else {
        Setting.showMessage("error", i18next.t("forget:Unknown forget type") + ": " + this.state.type);
      }
    }
  }

  getApplication() {
    if (this.state.applicationName === undefined) {
      return;
    }

    ApplicationBackend.getApplication("admin", this.state.applicationName)
      .then((res) => {
        if (res.status === "error") {
          Setting.showMessage("error", res.msg);
          return;
        }
        this.onUpdateApplication(res.data);
      });
  }

  getApplicationObj() {
    return this.props.application;
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  onOperationSelect(type) {
    this.setState({
      operationType: type,
      current: type === "changePassword" ? 0 : 1,
      isAtHomePage: false,
    });
  }

  onFormFinish(name, info, forms) {
    switch (name) {
    case "step1":
      forms.step1.validateFields()
        .then(values => {
          const username = values.username;
          AuthBackend.getEmailAndPhone(values.organization, username)
            .then((res) => {
              if (res.status === "ok") {
                const phone = res.data.phone;
                const email = res.data.email;

                if (!phone && !email) {
                  Setting.showMessage("error", "no verification method!");
                } else {
                  this.setState({
                    name: res.data.name,
                    phone: phone,
                    email: email,
                  });

                  const saveFields = (type, dest, fixed) => {
                    this.setState({
                      verifyType: type,
                      isVerifyTypeFixed: fixed,
                      dest: dest,
                    });
                  };

                  switch (res.data2) {
                  case "email":
                    saveFields("email", email, true);
                    break;
                  case "phone":
                    saveFields("phone", phone, true);
                    break;
                  case "username":
                    phone !== "" ? saveFields("phone", phone, false) : saveFields("email", email, false);
                  }

                  this.setState({
                    current: 2, // 修复：从 1 改为 2，与 renderForm 中的检查保持一致
                  });
                }
              } else {
                Setting.showMessage("error", res.msg);
              }
            });
        })
        .catch(error => {
          // 表单验证失败，Antd 会自动显示错误
        });
      break;
    case "step2":
      forms.step2.validateFields()
        .then(values => {
          UserBackend.verifyCode({
            application: values.application,
            organization: values.organization,
            username: values.dest,
            name: this.state.name,
            code: values.code,
            type: "login",
          }).then(res => {
            if (res.status === "ok") {
              if (this.state.operationType === "resetPassword") {
                this.setState({current: 3, code: values.code});
              } else if (this.state.operationType === "unlockAccount") {
                this.setState({current: 3, code: values.code});
              }
            } else {
              Setting.showMessage("error", res.msg);
            }
          });
        })
        .catch(error => {
          // 表单验证失败，Antd 会自动显示错误
        });
      break;
    case "changePasswordStep":
      this.handleChangePassword(forms);
      break;
    default:
      break;
    }
  }

  handleChangePassword() {
    this.form.current.validateFields()
      .then(values => {
        values.username = this.state.name;
        values.userOwner = this.getApplicationObj()?.organizationObj.name;
        UserBackend.setPassword(
          values.userOwner,
          values.username,
          values.oldPassword,
          values.newPassword,
          "",
          values.resetEmailPassword
        ).then(res => {
          if (res.status === "ok") {
            Setting.showMessage("success", i18next.t("forget:Password changed successfully"));
            const linkInStorage = sessionStorage.getItem("signinUrl");
            if (linkInStorage) {
              Setting.goToLinkSoft(this, linkInStorage);
            } else {
              Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
            }
          } else {
            Setting.showMessage("error", res.msg);
          }
        });
      })
      .catch(error => {
      });
  }

  unlockAccount() {
    UserBackend.unlockAccount(this.getApplicationObj()?.organizationObj.name, this.state.name, this.state.code)
      .then(res => {
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("forget:Account unlocked successfully"));
          const linkInStorage = sessionStorage.getItem("signinUrl");
          if (linkInStorage !== null && linkInStorage !== "") {
            Setting.goToLinkSoft(this, linkInStorage);
          } else {
            Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
          }
        } else {
          Setting.showMessage("error", res.msg);
        }
      });
  }

  onFinish(values) {
    // 确保表单验证通过
    this.form.current.validateFields()
      .then(validatedValues => {
        validatedValues.username = this.state.name;
        validatedValues.userOwner = this.getApplicationObj()?.organizationObj.name;
        UserBackend.setPassword(
          validatedValues.userOwner,
          validatedValues.username,
          "",
          validatedValues.newPassword,
          this.state.code,
          validatedValues.resetEmailPassword
        ).then(res => {
          if (res.status === "ok") {
            Setting.showMessage("success", i18next.t("forget:Password reset successfully"));
            const linkInStorage = sessionStorage.getItem("signinUrl");
            if (linkInStorage !== null && linkInStorage !== "") {
              Setting.goToLinkSoft(this, linkInStorage);
            } else {
              Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
            }
          } else {
            Setting.showMessage("error", res.msg);
          }
        });
      })
      .catch(error => {
        // 表单验证失败
      });
  }

  renderOptions() {
    const options = [];

    if (this.state.phone !== "") {
      options.push(
        <Option key={"phone"} value={this.state.phone} >
          &nbsp;&nbsp;{this.state.phone}
        </Option>
      );
    }

    if (this.state.email !== "") {
      options.push(
        <Option key={"email"} value={this.state.email} >
          &nbsp;&nbsp;{this.state.email}
        </Option>
      );
    }

    return options;
  }

  renderOperationSelection() {
    return (
      <div style={{width: "400px", margin: "0 auto"}}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              hoverable
              style={{textAlign: "center", cursor: "pointer"}}
              onClick={() => this.onOperationSelect("resetPassword")}
            >
              <KeyOutlined style={{fontSize: "32px", marginBottom: "16px"}} />
              <div style={{fontSize: "16px", fontWeight: "bold"}}>
                {i18next.t("forget:Reset password")}
              </div>
              <div style={{color: "#666", marginTop: "8px"}}>
                {i18next.t("forget:Reset password via email or SMS")}
              </div>
            </Card>
          </Col>
          <Col span={24}>
            <Card
              hoverable
              style={{textAlign: "center", cursor: "pointer"}}
              onClick={() => this.onOperationSelect("unlockAccount")} >
              <UnlockOutlined style={{fontSize: "32px", marginBottom: "16px"}} />
              <div style={{fontSize: "16px", fontWeight: "bold"}}>
                {i18next.t("forget:Unlock Account")}
              </div>
              <div style={{color: "#666", marginTop: "8px"}}>
                {i18next.t("forget:Unlock locked domain account")}
              </div>
            </Card>
          </Col>
          <Col span={24}>
            <Card
              hoverable
              style={{textAlign: "center", cursor: "pointer"}}
              onClick={() => this.onOperationSelect("changePassword")}
            >
              <LockOutlined style={{fontSize: "32px", marginBottom: "16px"}} />
              <div style={{fontSize: "16px", fontWeight: "bold"}}>
                {i18next.t("forget:Change Password")}
              </div>
              <div style={{color: "#666", marginTop: "8px"}}>
                {i18next.t("forget:Change password with old password")}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  renderChangePasswordForm(application) {
    return (
      <Form
        ref={this.form}
        name="changePasswordStep"
        onFinish={(values, forms) => this.onFormFinish("changePasswordStep", values, forms)}
        initialValues={{
          application: application.name,
          organization: application.organization,
          resetEmailPassword: false,
        }}
        style={{width: "300px"}}
        size="large"
      >
        <Form.Item
          hidden
          name="application"
          rules={[
            {
              required: true,
              message: i18next.t("application:Please input your application!"),
            },
          ]}
        />
        <Form.Item
          hidden
          name="organization"
          rules={[
            {
              required: true,
              message: i18next.t("application:Please input your organization!"),
            },
          ]}
        />
        <Form.Item
          name="username"
          rules={[
            {
              required: true,
              message: i18next.t("forget:Please input your username!"),
              whitespace: true,
            },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={i18next.t("login:username, Email or phone")}
            onChange={(e) => this.setState({name: e.target.value})}
          />
        </Form.Item>
        <Form.Item
          name="oldPassword"
          rules={[
            {
              required: true,
              message: i18next.t("forget:Please input your old password!"),
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={i18next.t("forget:Old Password")}
          />
        </Form.Item>
        <Popover placement={window.innerWidth >= 960 ? "right" : "top"} content={this.state.passwordPopover} open={this.state.passwordPopoverOpen}>
          <Form.Item
            name="newPassword"
            rules={[
              {
                required: true,
                validateTrigger: "onChange",
                validator: (rule, value) => {
                  const errorMsg = PasswordChecker.checkPasswordComplexity(value, application.organizationObj.passwordOptions);
                  if (errorMsg === "") {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(errorMsg);
                  }
                },
              },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={i18next.t("forget:New Password")}
              onChange={(e) => {
                this.setState({
                  passwordPopover: PasswordChecker.renderPasswordPopover(application.organizationObj.passwordOptions, e.target.value),
                });
              }}
              onFocus={() => {
                this.setState({
                  passwordPopoverOpen: application.organizationObj.passwordOptions?.length > 0,
                  passwordPopover: PasswordChecker.renderPasswordPopover(application.organizationObj.passwordOptions, this.form.current?.getFieldValue("newPassword") ?? ""),
                });
              }}
              onBlur={() => {
                this.setState({
                  passwordPopoverOpen: false,
                });
              }}
            />
          </Form.Item>
        </Popover>
        <Form.Item
          name="confirm"
          dependencies={["newPassword"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: i18next.t("signup:Please confirm your password!"),
            },
            ({getFieldValue}) => ({
              validator(rule, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  i18next.t("signup:Your confirmed password is inconsistent with the password!")
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<CheckCircleOutlined />}
            placeholder={i18next.t("signup:Confirm")}
          />
        </Form.Item>
        <Form.Item
          name="resetEmailPassword"
          valuePropName="checked"
        >
          <Checkbox>
            {i18next.t("forget:Also revise email password")}
          </Checkbox>
        </Form.Item>
        <br />
        <Form.Item>
          <Button block type="primary" htmlType="submit">
            {i18next.t("forget:Change Password")}
          </Button>
        </Form.Item>
        <Form.Item style={{textAlign: "right", marginTop: "0px"}}>
          <a
            style={{fontSize: "14px"}}
            onClick={() => {
              const currentUsername = this.form.current?.getFieldValue("username") || this.state.name;
              this.setState({
                operationType: "resetPassword",
                current: 1,
                isAtHomePage: false,
                name: currentUsername,
                username: currentUsername,
                phone: "",
                email: "",
                dest: "",
                isVerifyTypeFixed: false,
                verifyType: "",
              });
            }}
          >
            {i18next.t("forget:Forgot old password?")}
          </a>
        </Form.Item>
      </Form>
    );
  }

  renderForm(application) {
    if (this.state.current === 0 && this.state.operationType === "") {
      return null;
    }

    if (this.state.operationType === "changePassword") {
      return this.renderChangePasswordForm(application);
    }

    return (
      <Form.Provider onFormFinish={(name, {forms}) => {
        this.onFormFinish(name, {}, forms);
      }}>
        {/* STEP 1: input username -> get email & phone */}
        {this.state.current === 1 ?
          <Form
            ref={this.form}
            name="step1"
            initialValues={{
              application: application.name,
              organization: application.organization,
              username: this.state.name,
            }}
            style={{width: "300px"}}
            size="large"
          >
            <Form.Item
              hidden
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your application!"),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your organization!"),
                },
              ]}
            />
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: i18next.t("forget:Please input your username!"),
                  whitespace: true,
                },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={i18next.t("login:username, Email or phone")}
              />
            </Form.Item>
            <br />
            <Form.Item>
              <Button block type="primary" htmlType="submit">
                {i18next.t("forget:Next Step")}
              </Button>
            </Form.Item>
          </Form> : null}

        {/* STEP 2: verify email or phone */}
        {this.state.current === 2 ? <Form
          ref={this.form}
          name="step2"
          onValuesChange={(changedValues, allValues) => {
            if (!changedValues.dest) {
              return;
            }
            const verifyType = changedValues.dest?.indexOf("@") === -1 ? "phone" : "email";
            this.setState({
              dest: changedValues.dest,
              verifyType: verifyType,
            });
          }}
          initialValues={{
            application: application.name,
            organization: application.organization,
            dest: this.state.dest,
          }}
          style={{width: "300px"}}
          size="large"
        >
          <Form.Item
            style={{height: 0, visibility: "hidden"}}
            name="application"
            rules={[
              {
                required: true,
                message: i18next.t("application:Please input your application!"),
              },
            ]}
          />
          <Form.Item
            hidden
            name="organization"
            rules={[
              {
                required: true,
                message: i18next.t("application:Please input your organization!"),
              },
            ]}
          />
          <Form.Item
            name="dest"
            validateFirst
            hasFeedback
          >
            {
              <Select virtual={false}
                disabled={this.state.isVerifyTypeFixed}
                style={{textAlign: "left"}}
                placeholder={i18next.t("forget:Choose email or phone")}
              >
                {
                  this.renderOptions()
                }
              </Select>
            }
          </Form.Item>
          <Form.Item
            name="code"
            rules={[
              {
                required: true,
                message: i18next.t("code:Please input your verification code!"),
              },
            ]}
          >
            <SendCodeInput disabled={this.state.dest === ""}
              method={"forget"}
              onButtonClickArgs={[this.state.dest, this.state.verifyType, Setting.getApplicationName(this.getApplicationObj()), this.state.name]}
              application={application}
            />
          </Form.Item>
          <br />
          <Form.Item>
            <Button
              block
              type="primary"
              htmlType="submit"
            >
              {i18next.t("forget:Next Step")}
            </Button>
          </Form.Item>
        </Form> : null}

        {/* STEP 3: Reset Password (for resetPassword) */}
        {this.state.current === 3 && this.state.operationType === "resetPassword" ?
          <Form
            ref={this.form}
            name="step3"
            onFinish={(values) => this.onFinish(values)}
            initialValues={{
              application: application.name,
              organization: application.organization,
              resetEmailPassword: false,
            }}
            style={{width: "300px"}}
            size="large"
          >
            <Form.Item
              hidden
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your application!"),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your organization!"),
                },
              ]}
            />
            <Popover placement={window.innerWidth >= 960 ? "right" : "top"} content={this.state.passwordPopover} open={this.state.passwordPopoverOpen}>
              <Form.Item
                name="newPassword"
                rules={[
                  {
                    required: true,
                    validateTrigger: "onChange",
                    validator: (rule, value) => {
                      const errorMsg = PasswordChecker.checkPasswordComplexity(value, application.organizationObj.passwordOptions);
                      if (errorMsg === "") {
                        return Promise.resolve();
                      } else {
                        return Promise.reject(errorMsg);
                      }
                    },
                  },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={i18next.t("general:Password")}
                  onChange={(e) => {
                    this.setState({
                      passwordPopover: PasswordChecker.renderPasswordPopover(application.organizationObj.passwordOptions, e.target.value),
                    });
                  }}
                  onFocus={() => {
                    this.setState({
                      passwordPopoverOpen: application.organizationObj.passwordOptions?.length > 0,
                      passwordPopover: PasswordChecker.renderPasswordPopover(application.organizationObj.passwordOptions, this.form.current?.getFieldValue("newPassword") ?? ""),
                    });
                  }}
                  onBlur={() => {
                    this.setState({
                      passwordPopoverOpen: false,
                    });
                  }}
                />
              </Form.Item>
            </Popover>
            <Form.Item
              name="confirm"
              dependencies={["newPassword"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: i18next.t("signup:Please confirm your password!"),
                },
                ({getFieldValue}) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      i18next.t("signup:Your confirmed password is inconsistent with the password!")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<CheckCircleOutlined />}
                placeholder={i18next.t("signup:Confirm")}
              />
            </Form.Item>
            <Form.Item
              name="resetEmailPassword"
              valuePropName="checked"
            >
              <Checkbox>
                {i18next.t("forget:Also reset email password")}
              </Checkbox>
            </Form.Item>
            <br />
            <Form.Item>
              <Button block type="primary" htmlType="submit">
                {i18next.t("forget:Change Password")}
              </Button>
            </Form.Item>
          </Form> : null}

        {/* STEP 3: Unlock Account (for unlockAccount) */}
        {this.state.current === 3 && this.state.operationType === "unlockAccount" ?
          <div style={{width: "300px"}}>
            <div style={{textAlign: "center"}}>
              <p>{i18next.t("forget:Ready to unlock your account?")}</p>
            </div>
            <Button
              block
              type="primary"
              size="large"
              onClick={() => this.unlockAccount()}
            >
              {i18next.t("forget:Unlock Account")}
            </Button>
          </div> : null}
      </Form.Provider>
    );
  }

  getStepItems() {
    if (this.state.operationType === "changePassword") {
      return [
        {
          title: i18next.t("forget:Change Password"),
          icon: <LockOutlined />,
        },
      ];
    } else if (this.state.operationType === "resetPassword") {
      return [
        {
          title: i18next.t("forget:Account"),
          icon: <UserOutlined />,
        },
        {
          title: i18next.t("forget:Verify"),
          icon: <SolutionOutlined />,
        },
        {
          title: i18next.t("forget:Reset"),
          icon: <KeyOutlined />,
        },
      ];
    } else if (this.state.operationType === "unlockAccount") {
      return [
        {
          title: i18next.t("forget:Account"),
          icon: <UserOutlined />,
        },
        {
          title: i18next.t("forget:Verify"),
          icon: <SolutionOutlined />,
        },
        {
          title: i18next.t("forget:Unlock"),
          icon: <UnlockOutlined />,
        },
      ];
    }
    return [];
  }

  getAdjustedCurrent() {
    if (this.state.operationType === "changePassword") {
      return 0;
    } else {
      return this.state.current - 1;
    }
  }

  stepBack() {
    const {current, operationType} = this.state;
    if (current === 1 && operationType && !this.state.isAtHomePage) {
      this.setState({
        operationType: "",
        isAtHomePage: true,
        current: 0,
      });
    } else if (current > 1) {
      this.setState({
        current: current - 1,
      });
    } else if (current === 0 && operationType === "changePassword") {
      this.setState({
        operationType: "",
        isAtHomePage: true,
      });
    } else if (this.props.history.length > 1) {
      this.props.history.goBack();
    } else {
      Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
    }
  }

  render() {
    const application = this.getApplicationObj();
    if (application === undefined) {
      return null;
    }
    if (application === null) {
      return Util.renderMessageLarge(this, this.state.msg);
    }

    const stepItems = this.getStepItems();
    const adjustedCurrent = this.getAdjustedCurrent();

    const showBackButton = !this.state.isAtHomePage;

    return (
      <React.Fragment>
        <CustomGithubCorner />
        <div className="forget-content" style={{padding: Setting.isMobile() ? "0" : null, boxShadow: Setting.isMobile() ? "none" : null, paddingBottom: "72px"}}>
          {Setting.inIframe() || Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCss}} />}
          {Setting.inIframe() || !Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCssMobile}} />}
          {showBackButton && (
            <Button type="text"
              style={{position: "relative", left: Setting.isMobile() ? "10px" : "-90px", top: 0}}
              icon={<ArrowLeftOutlined style={{fontSize: "24px"}} />}
              size={"large"}
              onClick={() => {this.stepBack();}}
            />
          )}
          <Row>
            <Col span={24} style={{justifyContent: "center"}}>
              <Row>
                <Col span={24}>
                  <div style={{marginTop: "60px", textAlign: "center"}}>
                    {
                      Setting.renderHelmet(application)
                    }
                    {
                      Setting.renderLogo(application)
                    }
                  </div>
                </Col>
              </Row>
              {this.state.operationType && stepItems.length > 0 && (
                <Row>
                  <Col span={24}>
                    <Steps
                      current={adjustedCurrent}
                      items={stepItems}
                      direction="horizontal"
                      responsive={false}
                      labelPlacement={Setting.isMobile() ? "vertical" : "horizontal"}
                      style={{
                        width: Setting.isMobile() ? "95%" : "90%",
                        maxWidth: "500px",
                        margin: "auto",
                        marginTop: Setting.isMobile() ? "24px" : "24px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    />
                  </Col>
                </Row>
              )}
            </Col>
            <Col span={24} style={{display: "flex", justifyContent: "center"}}>
              <div style={{marginTop: "40px", textAlign: "center"}}>
                {this.state.operationType === "" ? this.renderOperationSelection() : this.renderForm(application)}
              </div>
            </Col>
          </Row>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(ForgetPage);
